import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ConsumptionEditor from "@/components/ConsumptionEditor";
import FollowupEditor from "@/components/FollowupEditor";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Sparkles, Plus, Pencil, Trash2, Loader2, Clock } from "lucide-react";
import { SERVICE_COLORS } from "@/lib/serviceColors";
import { toast } from "sonner";

const CATEGORIES = [
  "NETTOYAGE INTÉRIEUR", "NETTOYAGE EXTÉRIEUR", "POLISSAGE", "LUSTRAGE",
  "CÉRAMIQUE", "RÉNOVATION OPTIQUES", "TEXTILES", "AUTRES",
];
const EMPTY = { name: "", description: "", price: "", duration_minutes: "", category: "AUTRES", active: true, color: SERVICE_COLORS[0], pricing_type: "fixe", non_vehicle: false, vehicle_supplements: {}, show_public: true, public_price_visible: true };

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/services"); setServices(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/settings").then((r) => setCategories(r.data.vehicle_categories || [])).catch(() => {}); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...EMPTY, ...s, description: s.description ?? "", price: s.price ?? "", duration_minutes: s.duration_minutes ?? "", pricing_type: s.pricing_type || (s.price == null ? "sur_devis" : "fixe"), vehicle_supplements: s.vehicle_supplements || {} }); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error("Le nom est requis");
    setSaving(true);
    const payload = {
      name: form.name, description: form.description, category: form.category, active: form.active,
      color: form.color || null,
      price: form.price === "" ? null : Number(form.price),
      duration_minutes: form.duration_minutes === "" ? null : Number(form.duration_minutes),
      pricing_type: form.pricing_type || "fixe",
      non_vehicle: !!form.non_vehicle,
      vehicle_supplements: form.vehicle_supplements || {},
      show_public: form.show_public !== false,
      public_price_visible: form.public_price_visible !== false,
      consumptions: form.consumptions || [],
      workshop_types: form.workshop_types || [],
      followups: (form.followups || []).filter((r) => (r.label || "").trim() && Number(r.delay_days) > 0),
    };
    try {
      if (editing) await api.put(`/services/${editing.id}`, payload);
      else await api.post("/services", payload);
      toast.success(editing ? "Prestation mise à jour" : "Prestation créée");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/services/${id}`); toast.success("Prestation supprimée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const grouped = CATEGORIES.map((cat) => ({ cat, items: services.filter((s) => s.category === cat) })).filter((g) => g.items.length);

  return (
    <div>
      <PageHeader
        title="Prestations"
        subtitle="Catalogue de vos prestations — principalement sur devis"
        icon={Sparkles}
        actions={<Button onClick={openNew} data-testid="btn-new-service" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"><Plus className="w-4 h-4 mr-1.5" /> Nouvelle prestation</Button>}
      />

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
      ) : services.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">Aucune prestation.</div>
      ) : (
        <div className="space-y-6">
          {grouped.map((g) => (
            <div key={g.cat}>
              <h3 className="text-xs uppercase tracking-widest text-emerald-600/80 font-semibold mb-2">{g.cat}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {g.items.map((s) => (
                  <div key={s.id} data-testid={`service-card-${s.id}`} style={{ borderLeft: `4px solid ${s.color || "#e2e8f0"}` }} className={`bg-white border rounded-xl p-4 ${s.active ? "border-slate-200" : "border-slate-200 opacity-60"}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <span className="font-medium text-slate-900 flex items-center gap-2"><span className="w-3 h-3 rounded-full shrink-0" style={{ background: s.color || "#94a3b8" }} data-testid={`service-color-dot-${s.id}`} />{s.name}</span>
                      {!s.active && <Badge variant="outline" className="text-slate-500 border-slate-600 text-[10px]">Inactive</Badge>}
                    </div>
                    {s.description && <p className="text-xs text-slate-500 mb-2 line-clamp-2">{s.description}</p>}
                    <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                      <span className="font-mono text-emerald-600">{s.pricing_type === "sur_devis" || s.price == null ? "Sur devis" : s.pricing_type === "a_partir_de" ? `à partir de ${s.price} €` : `${s.price} €`}</span>
                      {s.duration_minutes != null && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{s.duration_minutes} min</span>}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`btn-edit-service-${s.id}`} className="flex-1 border-slate-200 bg-slate-50 h-8"><Pencil className="w-3.5 h-3.5 mr-1" /> Modifier</Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button size="sm" variant="outline" data-testid={`btn-delete-service-${s.id}`} className="border-red-500/30 bg-red-500/5 text-red-600 hover:bg-red-500/10 h-8 px-2"><Trash2 className="w-3.5 h-3.5" /></Button></AlertDialogTrigger>
                        <AlertDialogContent className="bg-white border-slate-200">
                          <AlertDialogHeader><AlertDialogTitle className="text-slate-900">Supprimer « {s.name} » ?</AlertDialogTitle><AlertDialogDescription>Action irréversible.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel className="bg-transparent border-slate-200">Annuler</AlertDialogCancel><AlertDialogAction onClick={() => del(s.id)} className="bg-red-500 hover:bg-red-600">Supprimer</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-slate-900">{editing ? "Modifier la prestation" : "Nouvelle prestation"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Nom *</Label><Input data-testid="input-service-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-50 border-slate-200" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-slate-50 border-slate-200" /></div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Type de tarification</Label>
              <Select value={form.pricing_type || "fixe"} onValueChange={(val) => setForm({ ...form, pricing_type: val })}>
                <SelectTrigger data-testid="select-service-pricing-type" className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-700">
                  <SelectItem value="fixe">Prix fixe</SelectItem>
                  <SelectItem value="a_partir_de">À partir de…</SelectItem>
                  <SelectItem value="sur_devis">Sur devis</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs text-slate-500">{form.pricing_type === "sur_devis" ? "Tarif (facultatif)" : form.pricing_type === "a_partir_de" ? "Tarif de base (€)" : "Tarif (€)"}</Label><Input type="number" data-testid="input-service-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder={form.pricing_type === "sur_devis" ? "Sur devis" : "0.00"} className="bg-slate-50 border-slate-200" /></div>
              <div className="space-y-1.5"><Label className="text-xs text-slate-500">Durée estimée (min)</Label><Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="bg-slate-50 border-slate-200" /></div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <div><Label className="text-sm text-slate-700">Prestation hors véhicule</Label><p className="text-[11px] text-slate-500">Canapé, matelas, tapis, bateau… — ignore le gabarit véhicule</p></div>
              <Switch checked={!!form.non_vehicle} onCheckedChange={(val) => setForm({ ...form, non_vehicle: val })} data-testid="switch-service-non-vehicle" />
            </div>
            {!form.non_vehicle && categories.length > 0 && (
              <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="service-supplements">
                <Label className="text-xs text-slate-500">Suppléments par gabarit véhicule (€)</Label>
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((c) => (
                    <div key={c.id} className="flex items-center gap-2">
                      <span className="text-xs text-slate-600 flex-1 truncate">{c.name}</span>
                      <Input type="number" step="0.01" value={(form.vehicle_supplements || {})[c.id] ?? ""} onChange={(e) => setForm({ ...form, vehicle_supplements: { ...(form.vehicle_supplements || {}), [c.id]: e.target.value === "" ? 0 : Number(e.target.value) } })} placeholder="+0" data-testid={`service-supplement-${c.id}`} className="w-20 h-8 bg-white border-slate-200 font-mono text-sm" />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!form.non_vehicle && categories.length === 0 && <p className="text-[11px] text-slate-400">Configurez vos gabarits dans Paramètres → Gabarits pour définir des suppléments par catégorie.</p>}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <Label className="text-sm text-slate-700">Page publique</Label>
                <Switch checked={form.show_public !== false} onCheckedChange={(val) => setForm({ ...form, show_public: val })} data-testid="switch-service-public" />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <Label className="text-sm text-slate-700">Afficher le tarif</Label>
                <Switch checked={form.public_price_visible !== false} onCheckedChange={(val) => setForm({ ...form, public_price_visible: val })} data-testid="switch-service-public-price" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="select-service-category" className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-700">{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Couleur de la prestation</Label>
              <div className="flex flex-wrap gap-2" data-testid="service-color-picker">
                {[...SERVICE_COLORS, ...(form.color && !SERVICE_COLORS.includes(form.color) ? [form.color] : [])].map((c) => (
                  <button type="button" key={c} data-testid={`color-${c}`} onClick={() => setForm({ ...form, color: c })}
                    className={`w-7 h-7 rounded-full transition-transform ${form.color === c ? "ring-2 ring-offset-2 ring-slate-400 scale-110" : "hover:scale-105"}`} style={{ background: c }} />
                ))}
              </div>
            </div>
            <div className="space-y-1.5" data-testid="service-workshop-types">
              <Label className="text-xs text-slate-500">Type d'intervention (adapte le dossier atelier)</Label>
              <div className="flex flex-wrap gap-2">
                {[["interieur", "Intérieur"], ["exterieur", "Lavage extérieur"], ["decontamination", "Décontamination"], ["polissage", "Polissage"], ["ceramique", "Céramique"], ["protection", "Cire / sealant"], ["optiques", "Optiques"], ["ppf", "PPF / PPS"]].map(([k, l]) => {
                  const on = (form.workshop_types || []).includes(k);
                  return <button type="button" key={k} data-testid={`wtype-${k}`} onClick={() => setForm({ ...form, workshop_types: on ? form.workshop_types.filter((x) => x !== k) : [...(form.workshop_types || []), k] })} className={`min-h-[36px] px-3 rounded-full text-xs border ${on ? "bg-emerald-500 text-black border-emerald-500" : "bg-white border-slate-200 text-slate-600"}`}>{l}</button>;
                })}
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <Label className="text-sm text-slate-700">Prestation active</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="switch-service-active" />
            </div>
          </div>
          <ConsumptionEditor value={form.consumptions || []} onChange={(v) => setForm({ ...form, consumptions: v })} />
          <FollowupEditor value={form.followups || []} onChange={(v) => setForm({ ...form, followups: v })} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-save-service" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
